import { useQuery } from '@tanstack/react-query';
import { getIndustryQuestions } from '../services/discoveryApi';
import { useGraphConsent } from './useGraphConsent';
import type { Industry, IndustryQuestions } from '../types/discovery.types';

/** Query key factory for industry question lookups. */
export const industryKeys = {
  all: ['industries'] as const,
  questions: (industry: Industry | '', role: string) =>
    [...industryKeys.all, 'questions', industry, role] as const,
};

/**
 * Fetch the pre-canned prompts + worked example for the selected industry.
 *
 * Disabled until an industry is chosen. The optional `role` frames the worked
 * example for the user's role (keyed into the cache so changing role refetches).
 * Content is static per (industry, role), so it is cached for the session.
 * Returns the standard `{ data, isLoading, error }`.
 */
export function useIndustryQuestions(industry: Industry | '', role = '') {
  const { acquireApiToken } = useGraphConsent();

  return useQuery<IndustryQuestions>({
    queryKey: industryKeys.questions(industry, role),
    queryFn: async () => {
      const token = await acquireApiToken();
      if (!token) {
        throw new Error('Could not acquire an access token to load questions.');
      }
      return getIndustryQuestions(industry as Industry, token, role);
    },
    enabled: industry !== '',
    staleTime: 60 * 60 * 1000, // 1 hour — content is static per (industry, role)
    retry: 1,
  });
}
